import { AllFacets, Entry, PlpProduct } from '@/types/plp'
import { AnalyticsPluginD } from '@/scripts/libs/vue-multianalytics/declarations'
import VirtualMirror from '@/scripts/libs/virtual-mirror/VirtualMirror'
import { ONE, ZERO } from '@/scripts/constants/booleans'
import { sortPromos } from '@/scripts/libs/sort-promos'
import head from 'lodash/head'
import {
  LENS_TYPE_DEFAULT,
  TRACKER_EVENT,
  TRACKER_LOADING_READY,
} from '@/scripts/constants/tracker'
import { RB } from '@/scripts/constants/store'
import PLP from '@/scripts/libs/algolia/plp'
import { FACET_KEYS, PRICE_FILTER } from '@/scripts/constants/plp'
import wcs from '@/scripts/libs/wcs'
import { AlgoliaPlpTrackD, AnalyticsFacetD } from '@/scripts/libs/algolia/types/plp'
export class AlgoliaPlpTrack implements AlgoliaPlpTrackD {
  public static generateModule() {
    if (window.algoliaPlpTrack) {
      return window.algoliaPlpTrack
    }

    return new AlgoliaPlpTrack()
  }
  public trackLoadingReady(products: Array<PlpProduct>, analyticsPlugin: AnalyticsPluginD): void {
    const productsToTrack = products.reduce((acc, productItem) => {
      const selectedColor = productItem.colors[0]
      const price = selectedColor.price && this.formatPriceForAnalytics(selectedColor.price)
      const sortedPromotions = sortPromos(selectedColor.promotions)
      const firstPromotion: { startDate: number; price: string; priceLabel: string } | undefined =
        head(sortedPromotions)
      let discountedPrice = price
      if (!!firstPromotion && PLP.isPromotionStarted(firstPromotion?.startDate || 0)) {
        discountedPrice =
          firstPromotion.price && this.formatPriceForAnalytics(firstPromotion.priceLabel)
      }
      acc = Object.assign({}, acc, {
        [selectedColor.partNumber]: {
          Status: selectedColor.status,
          Category: productItem.category,
          FrameType: selectedColor.frameType,
          LensType: LENS_TYPE_DEFAULT,
          Price: discountedPrice, //prezzo barrato
          PriceFull: price,
          Brand: RB,
          ModelName: selectedColor.name,
          MoCo: selectedColor.moCo,
          Stock: selectedColor.inventoryQuantity,
          Conf_IsUpcSupported: selectedColor.customizable,
          Preorder: selectedColor.preorder ? ONE : ZERO,
          Visibility: selectedColor.onSale,
        },
      })
      return acc
    }, {})
    analyticsPlugin?.trackEvent({
      event: TRACKER_LOADING_READY,
      value: {
        Products: productsToTrack,
      },
    })
  }
  getSelectedFacet(
    entry: Array<Entry>,
    facetKey: string,
    filterApplied: string,
    key: string,
    identifier: string
  ): Entry | undefined {
    const currentSelectedFacet = entry.find((fv) => {
      if (facetKey === FACET_KEYS.GENDER || facetKey === FACET_KEYS.POPULAR_FILTERS) {
        return fv.entryValue === filterApplied
      }
      return fv.identifier === identifier || fv.key === key
    })

    if (currentSelectedFacet) {
      return currentSelectedFacet ?? {}
    }
  }

  private formatPrice(price: string, decimalMatchPattern: RegExp) {
    return parseFloat(price.match(decimalMatchPattern)?.[0] || '0').toFixed(2)
  }

  public formatPriceForAnalytics(price: string): string {
    const matchPatternPoint = /[+-]?([0-9]*[.])?[0-9]+/
    const replacedPrice = price.replace('.', ',') || ''
    const commasCount = replacedPrice.match(/,/g)?.length || 0
    const splitPrice = replacedPrice.split(',') || []
    const postDecimalMatch = splitPrice[1]?.match(matchPatternPoint)
    const isPostDecimalValid = !!postDecimalMatch && postDecimalMatch[0]?.length > 2

    if (commasCount > 1) {
      return this.formatPrice(replacedPrice?.replace(',', ''), matchPatternPoint)
    } else if (commasCount > 0 && isPostDecimalValid) {
      const priceWithoutComma = price.replace(',', '').replace(',', '.')
      return this.formatPrice(priceWithoutComma, matchPatternPoint)
    } else {
      return this.formatPrice(price.replace(',', '.'), matchPatternPoint)
    }
  }

  public getPriceFacet(entry: Array<Entry> | undefined): AnalyticsFacetD {
    const findMap = (entry || [])
      .map((priceEntry) => {
        return parseInt(priceEntry.entryValue || '', 10)
      })
      .sort((a: number, b: number) => a - b)

    return {
      facetIdentifier: FACET_KEYS.PRICE,
      min: findMap[0] || 0,
      max: findMap[1] || 0,
    }
  }

  public appliedFilterToEntries(
    filterApplied: Array<string> | undefined,
    facets: AllFacets | undefined
  ): Array<AnalyticsFacetD | undefined> {
    let keyForPST: string
    if (facets && Object.keys(facets).length > 0) {
      const entriesFacets = Object.entries(facets || [])
      return (filterApplied || [])?.map((fa: string) => {
        let noAttr: string = fa
        if (fa.includes('.')) {
          const splitValue = fa.split('.')
          noAttr = splitValue[1]
          keyForPST = (splitValue[2] || '')?.split(':')[1] || ''
        }
        const [id, entryKey] = !Array.isArray(noAttr) ? noAttr.split(':') : noAttr.join('&facet=')
        const isPrice = id === PRICE_FILTER
        const findFacet = entriesFacets.reduce(
          (acc, entryFacet): Entry & { facetIdentifier?: string; min?: number; max?: number } => {
            const [facetKey, facet] = entryFacet
            if (Array.isArray(facet.entry)) {
              if (isPrice && facetKey === FACET_KEYS.PRICE) {
                return this.getPriceFacet(facet.entry) ?? acc
              }
              if (facetKey === id || facetKey === FACET_KEYS.POPULAR_FILTERS) {
                return this.getSelectedFacet(facet.entry, facetKey, fa, entryKey, id) ?? acc
              }
            }
            return acc
          },
          {}
        )
        if (findFacet) {
          return Object.assign(
            {},
            {
              facetIdentifier: id,
              PSTIdentifier: keyForPST,
            },
            findFacet
          )
        }
      })
    }
    return []
  }

  public buildAppliedFacetsAnalytics(
    mappedFacetIdentifier?: Array<string>,
    appliedOrderBy?: string
  ): string | undefined {
    const sortedAndMappedFacetIdentifier = mappedFacetIdentifier?.sort()
    const incompleteAppliedFacetsAnalytics = sortedAndMappedFacetIdentifier?.reduce(
      (previous, current) => {
        if (!previous) {
          return current
        }
        const [key, value] = current?.split('=')
        return previous.includes(key) ? `${previous},${value}` : `${previous}|${current}`
      },
      ''
    )
    const appliedOrderByFacetAnalytics = appliedOrderBy ? `SORT_BY=${appliedOrderBy}` : ''
    return appliedOrderByFacetAnalytics
      ? `${incompleteAppliedFacetsAnalytics}|${appliedOrderByFacetAnalytics}`
      : incompleteAppliedFacetsAnalytics
  }

  public mapFacetIdentifiers(
    appliedFilterToEntries: Array<AnalyticsFacetD | undefined> | undefined,
    currencySymbol: string
  ): Array<string> {
    return (appliedFilterToEntries || [])?.map((entries) => {
      if (entries) {
        const { facetIdentifier, key, label, mainLabel, max, min, PSTIdentifier } = entries
        if (facetIdentifier === FACET_KEYS.PRICE) {
          return `${facetIdentifier}=${currencySymbol}${min}-${currencySymbol}${max}`
        }
        if (PSTIdentifier) {
          return `${facetIdentifier}=${PSTIdentifier}`
        }
        if (mainLabel) {
          return `${mainLabel}=${facetIdentifier}`
        }
        if (label && key) {
          const attribute = this.getAttributeFromKey(key)
          return `${facetIdentifier}=${attribute}`
        }
        if (facetIdentifier) {
          return `${facetIdentifier}=null`
        }
      }
      return 'null=null'
    })
  }

  public getAttributeFromKey(key: string | undefined): string {
    if (key) {
      const attributes = key.split(',')
      const splitAttribute = attributes[0].split(':')
      return splitAttribute[0].replace('attributes_translated.', '')
    }
    return ''
  }

  public trackSearchEvent(
    totalResults: number | string,
    filterApplied: Array<string>,
    facets: AllFacets,
    analyticsPlugin: AnalyticsPluginD
  ): void {
    const appliedFilterToEntries = this.appliedFilterToEntries(filterApplied, facets) || []
    const { currencySymbol } = wcs.getStoreConfig()
    const appliedOrderBy = PLP.getAppliedOrderCriteria()
    const mappedFacetIdentifier = this.mapFacetIdentifiers(appliedFilterToEntries, currencySymbol)
    const appliedFacetsAnalytics = this.buildAppliedFacetsAnalytics(
      mappedFacetIdentifier,
      appliedOrderBy
    )
    const search_FacetValues_String = appliedFacetsAnalytics || appliedOrderBy

    analyticsPlugin?.trackEvent({
      event: TRACKER_EVENT,
      value: {
        Search_ResultItemsQnt: totalResults.toString(),
        Search_FacetValues_String: search_FacetValues_String?.toLowerCase() || '',
        Events_SearchFiltering: '1',
      },
    })
  }
}

window.algoliaPlpTrack = AlgoliaPlpTrack.generateModule()

export default window.algoliaPlpTrack
